import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch } from '@hooks/reduxHooks'
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'
import { Site } from '@services/api'
import {
  AcquisitionProtocolDto,
  CreateAcquisitionPoroctolDto,
} from '@services/dtos/AcquisitionProtocolDto'
import {
  doCreateAcquisitionProtocol,
  doUpdateAcquisitionProtocols,
} from '@state/reducers/acqProtocolReducer'
import { useSelector } from '@state/store'
import { CreateAcquisitionProtocolSchema } from '@utils/schemas'
import React, { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import UploadFile from '../UploadFile'

type Props = {
  editingAcquisitionProtocol: AcquisitionProtocolDto | undefined
  onClose: () => void
  defaultSiteId?: number
}

const AcquisitionProtocolForm: React.FC<Props> = ({
  onClose,
  editingAcquisitionProtocol,
  defaultSiteId,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateAcquisitionPoroctolDto>({
    resolver: yupResolver(CreateAcquisitionProtocolSchema),
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: editingAcquisitionProtocol
      ? {
          title: editingAcquisitionProtocol.title,
          description: editingAcquisitionProtocol.description,
          pdfUrl: editingAcquisitionProtocol.pdfUrl,
          siteIds: editingAcquisitionProtocol.sites?.map((s) => s.id),
        }
      : {
          siteIds: defaultSiteId ? [defaultSiteId] : [],
        },
  })

  const user = useSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const sites = useMemo(() => {
    return (user as any & { sites: Site[] })?.sites
  }, [user])

  const dispatch = useAppDispatch()

  const pdfFileUrl = watch('pdfUrl')
  const siteIds = watch('siteIds')

  const onSubmit = (values: CreateAcquisitionPoroctolDto) => {
    setIsSubmitting(true)
    const onSuccess = () => {
      onClose()
    }
    const stopSubmitting = () => {
      setIsSubmitting(false)
    }
    if (editingAcquisitionProtocol) {
      dispatch(
        doUpdateAcquisitionProtocols({
          id: editingAcquisitionProtocol.id,
          dto: values,
        }),
      )
        .then(onSuccess)
        .finally(stopSubmitting)
    } else {
      dispatch(doCreateAcquisitionProtocol(values))
        .then(onSuccess)
        .finally(stopSubmitting)
    }
  }

  const hasErrors = Object.keys(errors).length > 0

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              type="text"
              placeholder="Titre"
              {...register('title')}
              fullWidth
              size="small"
              label="Titre"
              error={!!errors.title?.message}
              helperText={
                errors.title?.message ? 'Veuillez saisir un titre' : ''
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="text"
              placeholder="Description"
              {...register('description')}
              size="small"
              fullWidth
              label="Description"
              error={!!errors.description}
              helperText={errors.description?.message}
            />
            <p>{errors.description?.message}</p>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <TextField
                size="small"
                label="Site"
                select
                SelectProps={{
                  multiple: true,
                }}
                {...register('siteIds')}
                value={siteIds}
                error={!!errors.siteIds}
                helperText={(errors?.siteIds as any)?.message}
              >
                {sites?.map((site: Site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <h3 className="text-lg font-bold mb-4">Fichier PDF associé</h3>
            <UploadFile
              accept="application/pdf"
              fileUrl={
                pdfFileUrl
                  ? `${process.env.REACT_APP_API_URL}/${pdfFileUrl}`
                  : undefined
              }
              onChange={(value) => setValue('pdfUrl', value)}
              label="Fichier PDF"
            />
          </Grid>
          <div className="flex flex-row gap-2 w-full justify-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Fermer
            </Button>
            <Button
              style={{
                backgroundColor: hasErrors ? '#3f3f3f20' : '#3f3f3f',
              }}
              variant="contained"
              type="submit"
              disabled={isSubmitting || hasErrors}
            >
              {isSubmitting && <CircularProgress size={20} className="mr-2" />}
              Enregistrer
            </Button>
          </div>
        </Grid>
      </form>
    </div>
  )
}

export default AcquisitionProtocolForm
